import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import useDownloadArchiveInstance from 'router/subrouters/DataRoom/hooks/useDownloadArchiveInstance';
import { checkIsArchiveSellSide, getFormattedDate } from '../../../utils';
import useCallbackAsync from 'hooks/useCallbackAsync';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { ArchiveInstanceProps } from '../ArchiveInstance/ArchiveInstance.component';

import './ArchiveDetails.styles.scss';

type ArchiveDetailsProps = {
  className?: string;
} & ArchiveInstanceProps &
  ModalProps;

const ArchiveDetails: React.ForwardRefRenderFunction<
  ModalRef,
  ArchiveDetailsProps
> = (props, ref) => {
  const { className, archiveInstance, updateArchive, ...rest } = props;

  const {
    _id: archiveInstanceId,
    dataRoomArchiveId,
    shareIndex,
    createdAt,
    hasActivityLogs,
    hasFiles,
    hasMembers,
    hasQA,
    fileType,
    type,
    isOldArchive,
    dataRoomBuySideGroup,
  } = archiveInstance || {};

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const { t } = useTranslation();

  const { isDownloading, handleDownload } = useDownloadArchiveInstance();

  const classes = classNames('archive-details', className);

  const isSellSide = useMemo(() => checkIsArchiveSellSide(type), [type]);

  const canShareIndex = useMemo(
    () => !shareIndex && !isSellSide && !isOldArchive,
    [shareIndex, isSellSide, isOldArchive],
  );

  const onClose = useCallback(() => modal?.close(), [modal]);

  const [shareWithBuySide, isSharingWithBuySide] = useCallbackAsync(
    async () => {
      const { data } = await dataRoomApi.archives.shareArchiveInstance(
        dataRoomArchiveId,
      );

      updateArchive(data);
      onClose();
    },
  );

  const areActionsDisabled = isDownloading || isSharingWithBuySide;

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('indexDetails')}
      {...rest}
    >
      <p>
        {t(isSellSide ? 'fullDataRoomIndexGeneratedOn' : 'indexGeneratedFor', {
          name: dataRoomBuySideGroup?.name,
          date: getFormattedDate(createdAt),
        })}
      </p>

      <div className="contains-wrapper">
        <p>{t('thisIndexContains')}</p>
        <ul>
          {hasFiles && <li>{t('files')}</li>}
          {hasQA && <li>{t('qAndA')}</li>}
          {hasActivityLogs && <li>{t('activityLog')}</li>}
          {hasMembers && <li>{t('members')}</li>}
        </ul>
      </div>

      <p>{t('fileTypeContains', { fileType: t(fileType) })}</p>

      {!isSellSide && <p>{t(shareIndex ? 'indexShared' : 'indexNotShared')}</p>}

      <div className="actions">
        {canShareIndex && (
          <Button
            variant="outline"
            disabled={areActionsDisabled}
            onClick={shareWithBuySide}
          >
            {t('shareIndexWithBuySide')}
          </Button>
        )}

        <Button
          disabled={areActionsDisabled}
          onClick={() =>
            handleDownload(dataRoomArchiveId, archiveInstanceId, onClose)
          }
        >
          {t('downloadIndex')}
        </Button>
      </div>
    </Modal>
  );
};

export default forwardRef(ArchiveDetails);
